import type { ComponentProps } from "react";
import { useMemo, useState } from "react";
import {
  useRecordContext,
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  SelectInput,
  BooleanInput,
  useRefresh,
  ReferenceInput,
  AutocompleteInput,
  Button,
  Create,
  useDelete,
  Confirm,
  useNotify,
  Toolbar,
  DeleteButton,
  SaveButton,
  usePermissions,
  useGetList,
  useUpdate,
  type RaRecord,
} from "react-admin";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Button as MuiButton,
  Tooltip,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { slotNameMap } from "@/constants";
import { purposeHelperText } from "@/utils/purpose-helper-text";
import { hasPermission } from "@/utils/permission-utils";
import { formatListDate } from "@/utils/date-format";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { hallSlotMatchesRazaEventDate } from "@/utils/hall-slot-matches-raza-event-date";

const razaEventAutocompleteText = (r: RaRecord) => {
  const d = r.date ? formatListDate(String(r.date)) : "";
  const t = r.title != null && String(r.title).trim() !== "" ? String(r.title) : "Untitled";
  const time = r.startTime ? ` · ${String(r.startTime)}` : "";
  return `${d} · ${t}${time}`;
};

const CustomToolbar = ({
  onClose,
  ...props
}: { onClose: () => void } & ComponentProps<typeof Toolbar>) => {
  const refresh = useRefresh();

  return (
    <Toolbar {...props} sx={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationOptions={{
          onSuccess: () => {
            onClose();
            refresh();
          },
        }}
      />
    </Toolbar>
  );
};

const HallBookingEditModal = ({
  id,
  open,
  onClose,
  bookingRazaRequestId,
}: {
  id: string | null;
  open: boolean;
  onClose: () => void;
  bookingRazaRequestId?: string | null;
}) => {
  const refresh = useRefresh();
  if (!id) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Hall Booking{" "}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Edit
          resource="hallBookings"
          id={id}
          mutationMode="pessimistic"
          redirect={false}
          actions={false}
          mutationOptions={{
            onSuccess: () => {
              onClose();
              refresh();
            },
          }}
        >
          <SimpleForm toolbar={<CustomToolbar onClose={onClose} />}>
            <TextInput source="hall.name" label="Hall" disabled fullWidth />
            <NoArrowKeyNumberInput source="thaals" label="Thaals" fullWidth />
            <ReferenceInput source="purpose" reference="bookingPurpose" fullWidth>
              <SelectInput optionText="name" fullWidth helperText={purposeHelperText("hallSlot")} />
            </ReferenceInput>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
              <DateInput source="date" label="Date" fullWidth />
              <MuiButton
                variant="text"
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ minWidth: 200, mt: 1 }}
                onClick={() => {
                  window.open("#/hallBookings", "_blank", "noopener,noreferrer");
                }}
              >
                Check hall availability
              </MuiButton>
            </Box>

            <SelectInput
              source="slot"
              label="Slot"
              choices={Object.entries(slotNameMap).map(([i, name]) => ({
                id: i,
                name,
              }))}
              fullWidth
            />
            <BooleanInput source="withAC" label="With AC" fullWidth />
            <TextInput source="remarks" label="Remarks" fullWidth multiline minRows={2} />
            {bookingRazaRequestId ? (
              <ReferenceInput
                source="razaEventId"
                reference="raza-events"
                filter={{ razaRequestId: bookingRazaRequestId, kind: "raza" }}
                allowEmpty
                perPage={100}
              >
                <AutocompleteInput
                  label="Event"
                  optionText={razaEventAutocompleteText}
                  emptyText="No event (unlink)"
                  fullWidth
                  helperText="Optional. Select an event to link this hall slot, or choose 'No event (unlink)'."
                />
              </ReferenceInput>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Link this booking to a raza request on the booking record to attach an event here.
              </Typography>
            )}
          </SimpleForm>
        </Edit>
      </DialogContent>
    </Dialog>
  );
};

const HallBookingCreateModal = ({
  open,
  onClose,
  bookingId,
  bookingRazaRequestId,
}: {
  open: boolean;
  onClose: () => void;
  bookingId: string | number | undefined;
  bookingRazaRequestId?: string | null;
}) => {
  const refresh = useRefresh();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create Hall Booking
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Create
          resource="hallBookings"
          redirect={false}
          mutationOptions={{
            onSuccess: () => {
              onClose();
              refresh();
            },
          }}
        >
          <SimpleForm defaultValues={{ bookingId }}>
            <ReferenceInput source="hallId" reference="halls">
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
            <ReferenceInput source="purpose" reference="bookingPurpose" fullWidth>
              <SelectInput optionText="name" fullWidth helperText={purposeHelperText("hallSlot")} />
            </ReferenceInput>
            <NoArrowKeyNumberInput source="thaals" label="Thaals" fullWidth />

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
              <DateInput source="date" label="Date" fullWidth />
              <MuiButton
                variant="text"
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ minWidth: 200, mt: 1 }}
                onClick={() => {
                  window.open("#/hallBookings", "_blank", "noopener,noreferrer");
                }}
              >
                Check hall availability
              </MuiButton>
            </Box>

            <SelectInput
              source="slot"
              label="Slot"
              choices={Object.entries(slotNameMap).map(([slotId, name]) => ({
                id: slotId,
                name,
              }))}
              fullWidth
            />
            <BooleanInput source="withAC" label="With AC" fullWidth />
            <TextInput source="remarks" label="Remarks" fullWidth multiline minRows={2} />
            {bookingRazaRequestId ? (
              <ReferenceInput
                source="razaEventId"
                reference="raza-events"
                filter={{ razaRequestId: bookingRazaRequestId, kind: "raza" }}
                allowEmpty
                perPage={100}
              >
                <AutocompleteInput
                  label="Event"
                  optionText={razaEventAutocompleteText}
                  emptyText="No event (unlink)"
                  fullWidth
                  helperText="Optional. Select an event to link this hall slot, or choose 'No event (unlink)'."
                />
              </ReferenceInput>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Link this booking to a raza request on the booking record to attach an event here.
              </Typography>
            )}
          </SimpleForm>
        </Create>
      </DialogContent>
    </Dialog>
  );
};

type HallBookingRow = {
  id: string;
  hall?: { name?: string };
  purpose?: string;
  withAC?: boolean;
  thaals?: number;
  date?: string;
  slot?: string;
  remarks?: string | null;
  razaEventId?: string | null;
};

type RazaEventOption = RaRecord & {
  id: string;
  title?: string | null;
  date?: string | null;
  startTime?: string | null;
};

export const HallBookingsShowTable = () => {
  const refresh = useRefresh();
  const notify = useNotify();
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const [editId, setEditId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [open, setOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState<HallBookingRow | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<RazaEventOption | null>(null);
  const [deleteOne] = useDelete();
  const [updateOne, { isLoading: isUpdatingLink }] = useUpdate();

  const hallBookings = ((record?.hallBookings as HallBookingRow[] | undefined) || []).slice();
  const bookingRazaRequestId = (record?.razaRequestId as string | null | undefined) ?? null;
  const showActions = hasPermission(permissions, "bookings.edit") && !record?.checkedOutOn;
  const tableColSpan = showActions ? 9 : 8;

  const { data: razaEvents = [], isLoading: isLoadingRazaEvents } = useGetList<RazaEventOption>(
    "raza-events",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "date", order: "ASC" },
      filter: bookingRazaRequestId
        ? { razaRequestId: bookingRazaRequestId, kind: "raza" }
        : { id: "__none__" },
    },
    { enabled: Boolean(bookingRazaRequestId) }
  );
  const eventById = useMemo(
    () => new Map(razaEvents.map((event) => [String(event.id), event])),
    [razaEvents]
  );

  if (!record) return null;

  const handleEdit = (id: string) => {
    setEditId(id);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditId(null);
  };

  const handleDelete = () => {
    if (!selectedId) return;
    deleteOne(
      "hallBookings",
      { id: selectedId, previousData: { id: selectedId } },
      {
        onSuccess: () => {
          notify("Hall booking deleted", { type: "info" });
          refresh();
        },
        onError: (error: unknown) => {
          notify(`Error: ${error instanceof Error ? error.message : String(error)}`, {
            type: "warning",
          });
        },
      }
    );
    setOpen(false);
  };

  const openLinkDialog = (row: HallBookingRow) => {
    const preselected =
      row.razaEventId != null && String(row.razaEventId).trim() !== ""
        ? (eventById.get(String(row.razaEventId)) ?? null)
        : null;
    setSelectedEvent(preselected);
    setLinkTarget(row);
    setLinkDialogOpen(true);
  };

  const closeLinkDialog = () => {
    if (isUpdatingLink) return;
    setLinkDialogOpen(false);
    setLinkTarget(null);
    setSelectedEvent(null);
  };

  const saveLinkedEvent = (nextRazaEventId: string | null) => {
    if (!linkTarget?.id) return;
    updateOne(
      "hallBookings",
      {
        id: linkTarget.id,
        data: { razaEventId: nextRazaEventId },
        previousData: linkTarget,
      },
      {
        onSuccess: () => {
          notify(nextRazaEventId ? "Raza event linked" : "Raza event unlinked", {
            type: "success",
          });
          refresh();
          closeLinkDialog();
        },
        onError: (error: unknown) => {
          notify(`Error: ${error instanceof Error ? error.message : String(error)}`, {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <Box mb={0}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Hall Bookings</Typography>
        {showActions && (
          <Button variant="outlined" color="primary" onClick={() => setOpenCreate(true)}>
            Add Hall
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Hall</TableCell>
            <TableCell>Purpose</TableCell>
            <TableCell>AC</TableCell>
            <TableCell>Thaals</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Slot</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Remarks</TableCell>
            {showActions && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {hallBookings.length > 0 ? (
            hallBookings
              .slice()
              .sort((a, b) => new Date(a.date ?? "").getTime() - new Date(b.date ?? "").getTime())
              .map((hb) => (
                <TableRow key={hb.id}>
                  <TableCell>{hb.hall?.name}</TableCell>
                  <TableCell>{hb.purpose}</TableCell>
                  <TableCell>{hb.withAC ? "With AC" : "W/O AC"}</TableCell>
                  <TableCell>{hb.thaals}</TableCell>
                  <TableCell>{hb.date ? formatListDate(hb.date) : ""}</TableCell>
                  <TableCell>{slotNameMap[hb.slot ?? ""] ?? hb.slot}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {hb.razaEventId ? (
                      <Typography variant="body2">
                        {razaEventAutocompleteText(
                          eventById.get(String(hb.razaEventId)) ?? { id: hb.razaEventId }
                        )}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {hb.remarks ? (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {hb.remarks}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell align="right">
                      <Tooltip
                        title={
                          bookingRazaRequestId
                            ? ""
                            : "Link a booking-level raza request first to assign hall events."
                        }
                      >
                        <span>
                          <MuiButton
                            size="small"
                            variant="text"
                            onClick={() => openLinkDialog(hb)}
                            disabled={!bookingRazaRequestId}
                          >
                            {hb.razaEventId ? "Change event" : "Link event"}
                          </MuiButton>
                        </span>
                      </Tooltip>
                      <IconButton onClick={() => handleEdit(hb.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedId(hb.id);
                          setOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={tableColSpan} align="center">
                No hall bookings found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Confirm
        isOpen={open}
        title="Delete Hall Booking"
        content="Are you sure you want to permanently delete this hall booking?"
        onConfirm={handleDelete}
        onClose={() => setOpen(false)}
      />

      <HallBookingEditModal
        id={editId}
        open={modalOpen}
        onClose={handleClose}
        bookingRazaRequestId={record.razaRequestId as string | null | undefined}
      />
      <HallBookingCreateModal
        open={openCreate}
        bookingId={record.id}
        onClose={() => setOpenCreate(false)}
        bookingRazaRequestId={record.razaRequestId as string | null | undefined}
      />

      <Dialog open={linkDialogOpen} onClose={closeLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {linkTarget?.hall?.name ? `Link event for ${linkTarget.hall.name}` : "Link event"}
        </DialogTitle>
        <DialogContent>
          {!bookingRazaRequestId ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Link a raza request on this booking before assigning an event to hall lines.
            </Typography>
          ) : (
            <>
              <Autocomplete
                options={razaEvents}
                loading={isLoadingRazaEvents}
                value={selectedEvent}
                onChange={(_, value) => setSelectedEvent(value)}
                getOptionLabel={razaEventAutocompleteText}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                renderInput={(params) => (
                  <TextField {...params} label="Raza event" margin="dense" fullWidth />
                )}
                sx={{ mt: 1 }}
              />
              {selectedEvent &&
              linkTarget?.date &&
              !hallSlotMatchesRazaEventDate(linkTarget.date, selectedEvent.date) ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Hall date ({formatListDate(linkTarget.date, { empty: "—" })}) must match the event
                  date ({formatListDate(selectedEvent.date, { empty: "—", utc: true })}). Choose a
                  different event or edit the hall date first.
                </Alert>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeLinkDialog} disabled={isUpdatingLink}>
            Cancel
          </MuiButton>
          <MuiButton
            color="warning"
            onClick={() => saveLinkedEvent(null)}
            disabled={isUpdatingLink || !linkTarget?.razaEventId}
          >
            Unlink
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={() => saveLinkedEvent(selectedEvent ? String(selectedEvent.id) : null)}
            disabled={
              isUpdatingLink ||
              !bookingRazaRequestId ||
              (Boolean(selectedEvent) &&
                Boolean(linkTarget?.date) &&
                !hallSlotMatchesRazaEventDate(linkTarget?.date, selectedEvent?.date))
            }
          >
            {linkTarget?.razaEventId ? "Save" : "Link"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HallBookingsShowTable;
